import React, { useState, useEffect, FC, useContext } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  hexToRgb,
  useTheme,
  ThemeProvider,
  CssBaseline,
  LinearProgress,
  Typography,
} from '@mui/material';
import SurveyFooter from '../../components/SurveyFooter/SurveyFooter';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import SurveyHeader from '../../components/SurveyHeader/SurveyHeader';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import StandardSurveyContent from '../../components/StandardSurveyContent/StandardSurveyContent';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import i18n from 'i18next';
import {
  scopeSurveyCustomCss,
  SURVEY_CUSTOM_CSS_SCOPE_CLASS,
  SURVEY_CUSTOM_CSS_SCOPE_SELECTOR,
} from '../../shared/SurveyCustomCss';

interface Props {
  previewSurvey?: ISurvey & {
    shouldShowPrivacyNotice: boolean;
    privacyNoticeText: string;
    privacyNoticeLink: string;
  };
}

const StandardSurveyPage: FC<Props> = ({ previewSurvey }) => {
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();

  const [error, setError] = useState<boolean>(false);

  const [standardSurveyPage, setStandardSurveyPage] = useState<
    | (ISurvey & {
        shouldShowPrivacyNotice: boolean;
        privacyNoticeText: string;
        privacyNoticeLink: string;
      })
    | null
  >(previewSurvey ?? null);
  const localTranslation = useLocalTranslation(
    standardSurveyPage ? standardSurveyPage : { lang: 'de' }
  );

  const [currentId, setCurrentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (previewSurvey) return;
    const controller = new AbortController();
    const splitUrl = window.location.href.split('/');
    const extractValue = splitUrl[splitUrl.length - 1].toString();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurveyPublic(
          extractValue,
          'standard',
          setLoading,
          controller.signal
        );
        if (response) {
          setCurrentId(response.id);
          setStandardSurveyPage(response);
          console.log('Response', response);
          if (response.language) {
            i18n.changeLanguage(response.language);
          }
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Fehler beim Abrufen der Umfrage:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, [currentId, previewSurvey]);

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = standardSurveyPage?.label
      ? standardSurveyPage?.label
      : `${t('app_branding')} ${t('survey')}`;
  }, [setCurrentView, t, standardSurveyPage]);

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  if (standardSurveyPage) {
    const effectiveFontFamily =
      standardSurveyPage.customFont && standardSurveyPage.customFontFormat
        ? `"${standardSurveyPage.id}"`
        : '"Arial", "Helvetica", sans-serif';
    const scopedCustomCss = scopeSurveyCustomCss(standardSurveyPage.customCss);
    const customFontCss =
      standardSurveyPage.customFont && standardSurveyPage.customFontFormat
        ? `
            @font-face {
              font-family: "${standardSurveyPage.id}";
              src: url(${standardSurveyPage.customFont}) format("${getFontFormatByFileExtension(
            standardSurveyPage.customFontFormat as FontFileExtensions
          )}");
            }

            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `
        : `
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `;
    const customTheme = createTheme({
      palette: {
        primary: {
          main: standardSurveyPage.primaryColor
            ? standardSurveyPage.primaryColor
            : theme.palette.primary.main,
        },
        divider: standardSurveyPage.secondaryColor
          ? alpha(hexToRgb(standardSurveyPage.secondaryColor), 0.2)
          : theme.palette.divider,
      },
      typography: {
        fontFamily: effectiveFontFamily,
      },
    });

    return (
      <LocalTranslationContext.Provider value={localTranslation}>
        <CssBaseline />
        <Box
          className="survey-page-shell survey-page-shell--standard"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            backgroundColor: '#ffffff',
          }}
        >
          <ThemeProvider theme={customTheme}>
            <style>{customFontCss}</style>
            {scopedCustomCss && <style>{scopedCustomCss}</style>}
            {loading && <LinearProgress />}
            <Box
              className={SURVEY_CUSTOM_CSS_SCOPE_CLASS}
              sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <Box
                className="survey-page survey-page--standard standard-survey-page-root"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100%',
                  flex: 1,
                  fontFamily: effectiveFontFamily,
                }}
              >
                <SurveyHeader
                  logoLink={standardSurveyPage.logoLink}
                  logo={standardSurveyPage.companyLogo}
                />
                <StandardSurveyContent {...standardSurveyPage} />
              </Box>
            </Box>
          </ThemeProvider>

          <SurveyFooter
            company={standardSurveyPage.companyName}
            privacyPolicyLink={standardSurveyPage.privacyPolicyLink}
            imprintLink={standardSurveyPage.imprintLink}
          />
        </Box>
      </LocalTranslationContext.Provider>
    );
  } else {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {error ? (
            <Typography>{t('survey_not_found_or_archived')}</Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Section>
    );
  }
};

export default StandardSurveyPage;
